import { useMemo } from 'react'
import { motion } from 'framer-motion'

import { Button } from '@/components/ui/button'
import { IconTrendingUp } from '@tabler/icons-react'

const MAX_CONVERSATION_STARTERS = 4

interface ConversationStarter {
  display: string
  input: string
  trend?: any
  isTrending?: boolean
}

export function ConversationStarters({
  values,
  starters,
  onSelect,
  metadata
}: {
  values?: Array<string>
  starters?: Array<ConversationStarter>
  onSelect: (starter: string) => void
  metadata?: {
    personalizedCount: number
    trendingCount: number
    sources: string[]
  }
}) {
  // Use starters if available, otherwise fallback to values as strings
  const starterItems = starters?.length
    ? starters
    : (values?.map((v) => ({ display: v, input: v, isTrending: false })) ?? [])
  const hash = starterItems
    .map((s) => s.display + s.input + (s.isTrending ? 'trending' : 'normal'))
    .join('')

  return useMemo(() => {
    const trimmedStarters = [...starterItems]
      // Remove random shuffling to maintain personalized + trending order
      // ?.sort(() => Math.random() - 0.5)
      // pick the first {MAX_CONVERSATION_STARTERS} starters
      .slice(0, MAX_CONVERSATION_STARTERS)

    return (
      <div className="w-full space-y-3">
        <div className="grid min-h-[120px] w-full max-w-none grid-cols-1 gap-3 sm:grid-cols-2">
          {trimmedStarters?.map((starter, index) => (
            <motion.div
              key={`${starter.display}-${index}`}
              initial={{ opacity: 0, y: 15, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                delay: 0.05 * index,
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }}
            >
              <Button
                onClick={() => onSelect(starter.input)}
                variant="ghost"
                title={`${starter.display} - Click to ask: "${starter.input}"`}
                className={`border-border/40 from-background via-background to-muted/30 hover:border-primary/20 hover:from-primary/5 hover:via-background hover:to-primary/10 focus-visible:ring-primary/50 group relative h-auto w-full min-w-0 max-w-full rounded-xl border bg-gradient-to-br px-3 py-2.5 text-left text-sm font-normal shadow-sm transition-all duration-200 ease-out hover:bg-gradient-to-br hover:shadow-md focus-visible:ring-2`}
              >
                <div className="flex min-w-0 items-start gap-2.5">
                  {starter.isTrending && (
                    <div className="mt-0.5 shrink-0 opacity-60 transition-opacity group-hover:opacity-100">
                      <IconTrendingUp
                        size={14}
                        className="text-primary/70 group-hover:text-primary"
                      />
                    </div>
                  )}
                  <span className="text-foreground group-hover:text-foreground/90 block min-w-0 truncate leading-snug">
                    {starter.display}
                  </span>
                </div>

                <div className="via-primary/5 absolute inset-0 rounded-xl bg-gradient-to-r from-transparent to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    )
  }, [hash])
}
